import { FunctionComponent, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ButtonPrimary,
  ButtonTertiary,
  FormInput,
  FormInputIcon,
  IconCheck,
  LoadingIndicator,
  Type,
} from '@spotify-internal/encore-web';

import { ErrorMessage } from 'components/ContentReporting/EmailVerification/EmailVerificationDialog';
import EmailField from 'components/Fields/EmailField/EmailField';
import analytics, {
  StepLabel,
} from 'components/ContentReporting/ContentPolicyReporting/policy-reporting-analytics-utils';

import { DialogBodyWrapper, StyledDialogConfirmation } from './EmailVerification.styles';

const getEmailSentBody = (t: (key: string) => string): JSX.Element => {
  return (
    <FormInputIcon iconTrailing={<IconCheck semanticColor="textBrightAccent" />}>
      <FormInput
        id="sent"
        name="sent"
        type="text"
        value={t('email_verification_email_sent')}
        disabled
        style={{ cursor: 'default' }}
      />
    </FormInputIcon>
  );
};

const getEmailInputBody = (
  t: (key: string) => string,
  email: string,
  emailValid: boolean,
  setEmail: React.Dispatch<React.SetStateAction<string>>,
  setEmailValid: React.Dispatch<React.SetStateAction<boolean>>,
  error: string,
  disabled: boolean,
): JSX.Element => {
  const errorMessageKey = error ? error : ErrorMessage.EMAIL_INVALID;
  const valid = emailValid && !error;

  return (
    <EmailField
      field={{
        name: 'email',
        type: 'email',
        maxlength: 80,
        labelOverride: ' ',
        placeholderOverride: t('email_verification_email_placeholder'),
      }}
      value={email}
      valid={valid}
      errorMessageKey={t(errorMessageKey)}
      onFieldChange={(change: any) => {
        setEmail(change.value);
        setEmailValid(change.valid);
      }}
      disabled={disabled}
      forceValidation
      validateOnChange
    />
  );
};

export interface Step1EnterEmailProps {
  email: string;
  emailValid: boolean;
  emailSent: boolean;
  isLoggedInWithEmail: boolean;
  loading: boolean;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setEmailValid: React.Dispatch<React.SetStateAction<boolean>>;
  error: string;
  onCancel: () => void;
  onClick: (event: React.MouseEvent<any, MouseEvent>) => void;
}

const Step1EnterEmail: FunctionComponent<Step1EnterEmailProps> = (props): JSX.Element => {
  const {
    email,
    emailValid,
    emailSent,
    isLoggedInWithEmail,
    loading,
    setEmail,
    setEmailValid,
    error,
    onCancel,
    onClick,
  } = props;

  const { t } = useTranslation('content_policy_reporting');
  const location = useLocation();

  useEffect(
    () => analytics.trackVirtualPageView(location.pathname, location.search, StepLabel.EmailVerificationEnterEmail),
    [location.pathname, location.search],
  );

  const disabled: boolean = !emailValid || !email || loading;
  const disabledEmailField: boolean = loading || isLoggedInWithEmail;
  return (
    <StyledDialogConfirmation
      dialogTitle={t('email_verification_email_title')}
      body={
        <DialogBodyWrapper>
          {/* The dangerouslySetInnerHTML was added because we have bold html tag inside the
      translation text for the email_verification_email_label label. Because of this the
      labelOverride needs to be empty to not render. A bit hacky solution to be able to
      implement the required design. */}
          <Type
            dangerouslySetInnerHTML={{
              __html: t('email_verification_email_label'),
            }}
          />
          {emailSent
            ? getEmailSentBody(t)
            : getEmailInputBody(t, email, emailValid, setEmail, setEmailValid, error, disabledEmailField)}
        </DialogBodyWrapper>
      }
      $hideFooter={emailSent}
      footer={
        <div>
          <ButtonTertiary buttonSize="sm" onClick={onCancel}>
            {t('email_verification_cancel_button')}
          </ButtonTertiary>
          <ButtonPrimary disabled={disabled} buttonSize="sm" onClick={onClick}>
            {loading ? <LoadingIndicator indicatorSize="sm" /> : t('email_verification_email_verification_button')}
          </ButtonPrimary>
        </div>
      }
    />
  );
};

export default Step1EnterEmail;
